import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";

const MAX_MS = 30000;

export function PhotoVoiceNote({ photoId, initialTranscript = "" }) {
  const [text, setText] = useState(initialTranscript || "");
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false); // uploading + transcribing
  const [elapsed, setElapsed] = useState(0);
  const [manualOnly, setManualOnly] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const capRef = useRef(null);
  const savedRef = useRef(initialTranscript || "");

  useEffect(() => () => cleanup(), []); // unmount

  const cleanup = () => {
    clearInterval(timerRef.current);
    clearTimeout(capRef.current);
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };

  const saveNote = useCallback(async (value) => {
    const v = (value ?? "").trim();
    if (v === (savedRef.current || "").trim()) return;
    try {
      await api.patch(`/photos/${photoId}/note`, { voice_note_transcript: v });
      savedRef.current = v;
    } catch (e) {
      console.error("save photo note failed", e);
      toast.error("Couldn't save the note — try again.");
    }
  }, [photoId]);

  const upload = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "note.webm");
      const { data } = await api.post(`/photos/${photoId}/voice-note`, fd);
      const t = data.voice_note_transcript || "";
      setText(t);
      savedRef.current = t;
      if (t) toast.success("Voice note added.");
      else toast("No speech detected — you can type a note instead.");
    } catch (e) {
      console.error("voice-note transcription failed", e);
      setManualOnly(true);
      toast.error("Couldn't transcribe — type your note instead.");
    } finally {
      setBusy(false);
    }
  };

  const startRecording = async () => {
    if (recording || busy) return;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      console.error("mic permission denied", e);
      setManualOnly(true);
      toast.error("Microphone unavailable — type your note instead.");
      return;
    }
    streamRef.current = stream;
    chunksRef.current = [];
    let rec;
    try {
      rec = new MediaRecorder(stream);
    } catch (e) {
      console.error("MediaRecorder unsupported", e);
      cleanup();
      setManualOnly(true);
      toast.error("Recording not supported — type your note instead.");
      return;
    }
    recRef.current = rec;
    rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunksRef.current.push(ev.data); };
    rec.onstop = () => {
      cleanup();
      setRecording(false);
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      if (blob.size > 0) upload(blob);
    };
    rec.start();
    setRecording(true);
    setElapsed(0);
    const startedAt = Date.now();
    timerRef.current = setInterval(() => setElapsed(Date.now() - startedAt), 200);
    capRef.current = setTimeout(() => stopRecording(), MAX_MS);
  };

  const stopRecording = () => {
    clearTimeout(capRef.current);
    clearInterval(timerRef.current);
    if (recRef.current && recRef.current.state !== "inactive") {
      recRef.current.stop();
    }
  };

  const secs = Math.min(30, Math.floor(elapsed / 1000));

  return (
    <div className="mt-1.5 px-0.5" data-testid={`voice-note-${photoId}`}>
      <div className="flex items-start gap-1.5">
        {!manualOnly && (
          <button
            type="button"
            data-testid={`voice-mic-${photoId}`}
            disabled={busy}
            aria-label={recording ? "Stop recording" : "Hold to record voice note"}
            onPointerDown={(e) => { e.preventDefault(); startRecording(); }}
            onPointerUp={(e) => { e.preventDefault(); if (recording) stopRecording(); }}
            onPointerLeave={() => { if (recording) stopRecording(); }}
            onContextMenu={(e) => e.preventDefault()}
            className={`grid place-items-center h-9 w-9 rounded-full shrink-0 select-none touch-none transition-colors ${
              recording ? "bg-[#DC2626] text-white animate-pulse"
              : busy ? "bg-white/10 text-white/50"
              : "bg-white/10 text-white/80 hover:bg-white/20 active:bg-brand"
            }`}
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : recording ? <Square size={13} /> : <Mic size={15} />}
          </button>
        )}
        <textarea
          data-testid={`voice-note-input-${photoId}`}
          value={busy ? "Transcribing…" : text}
          disabled={busy}
          onChange={(e) => setText(e.target.value)}
          onBlur={(e) => saveNote(e.target.value)}
          rows={2}
          placeholder={recording ? `Recording… ${secs}s / 30s` : "Add a note (or hold the mic)"}
          className="flex-1 min-w-0 resize-none rounded-lg bg-white/5 border border-white/10 text-white text-xs px-2.5 py-1.5 placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-brand"
        />
      </div>
      {recording && (
        <p className="mt-1 text-[10px] font-medium text-[#FCA5A5]" data-testid={`voice-recording-${photoId}`}>
          ● Recording {secs}s — release to transcribe (max 30s)
        </p>
      )}
    </div>
  );
}
